/**
 * Photo-centric Order pane model (round 5). The Step-2 "Your order" pane lists
 * PHOTOS, not size lines: each ordered photo is a small thumbnail row with a
 * compact summary of its prints. Photos sharing an IDENTICAL print
 * configuration collapse into ONE stacked group row, so a 200-photo wizard
 * order stays a single row. Pure derivations over UiPrint[] — no React, no
 * I/O — unit-tested in photoOrder.test.ts.
 *
 * CONFIG IDENTITY: the multiset of (sizeId, paper, qty, borderMode, frameId,
 * bw) across a photo's prints. Order of prints does not matter; quantities do.
 * Rotation and crop are per-photo geometry, so they do NOT split rows.
 */

import { type PricingData } from '@rpl/pricing';
import {
  chipBorderTag,
  isBlockedPrint,
  isFlaggedPrint,
  shortPaperLabel,
  sizeIdShort,
} from '../../lib/sizePaper';
import type { UiPrint } from '../../lib/types';

/** Canonical signature of ONE photo's print configuration (multiset). */
export function configSignature(prints: readonly UiPrint[]): string {
  return prints
    .map(
      (p) =>
        `${p.sizeId}|${p.paper}|${p.qty}|${p.frameId ?? ''}|${p.frameId ? 'none' : p.borderMode ?? 'none'}|${
          p.bw ? 'bw' : 'c'
        }`,
    )
    .sort()
    .join('||');
}

export interface PhotoOrderRow {
  /** Stable row key: the config signature (group rows) + first photo id. */
  key: string;
  /** Photos sharing this exact config, in first-seen prints order. */
  photoIds: string[];
  /** The FIRST photo's prints (the representative config, for summaries/actions). */
  configPrints: UiPrint[];
  /** All member prints across the row's photos. */
  prints: UiPrint[];
  /** Priced row total across all photos (blocked prints excluded). */
  total: number;
  /** Prints in this row that still need attention (unresolved crop / blocked). */
  attention: number;
  /**
   * BLOCKED prints (low-res, no override) — in the row but EXCLUDED from the
   * total until fixed. Surfaced as the amber "N need review · not counted
   * yet" line so price × count always reconciles for the customer.
   */
  blocked: number;
  /** Photos whose prints are ALL priced — the headline count on group rows. */
  pricedPhotoCount: number;
}

/**
 * Group ordered photos into rows: photos with IDENTICAL configs collapse into
 * one row (first-seen order); unique configs get their own row. Photos with no
 * prints don't appear (they live in the Photos pane's to-do pool).
 */
export function photoOrderRows(prints: readonly UiPrint[]): PhotoOrderRow[] {
  // Per-photo buckets in first-seen order.
  const byPhoto = new Map<string, UiPrint[]>();
  for (const p of prints) {
    if (p.qty <= 0) continue;
    const list = byPhoto.get(p.photoId);
    if (list) list.push(p);
    else byPhoto.set(p.photoId, [p]);
  }
  // Collapse identical configs.
  const rows = new Map<string, PhotoOrderRow>();
  for (const [photoId, photoPrints] of byPhoto) {
    const sig = configSignature(photoPrints);
    const existing = rows.get(sig);
    if (existing) {
      existing.photoIds.push(photoId);
      existing.prints.push(...photoPrints);
    } else {
      rows.set(sig, {
        key: `${sig}::${photoId}`,
        photoIds: [photoId],
        configPrints: photoPrints,
        prints: [...photoPrints],
        total: 0,
        attention: 0,
        blocked: 0,
        pricedPhotoCount: 0,
      });
    }
  }
  // Totals + attention + priced-vs-blocked reconciliation.
  const out = [...rows.values()];
  for (const row of out) {
    let total = 0;
    let attention = 0;
    let blocked = 0;
    const blockedPhotos = new Set<string>();
    for (const p of row.prints) {
      if (isFlaggedPrint(p)) attention += 1;
      if (isBlockedPrint(p)) {
        blocked += 1;
        blockedPhotos.add(p.photoId);
      } else {
        total += (p.unitPrice ?? 0) * p.qty;
      }
    }
    row.total = total;
    row.attention = attention;
    row.blocked = blocked;
    row.pricedPhotoCount = row.photoIds.filter((id) => !blockedPhotos.has(id)).length;
  }
  return out;
}

/**
 * Compact one-line summary of a photo's config, e.g.
 * `4×6 (2) Gloss · 8×10 (1) Gloss WB · Custom 11.5×17 (1) Pearl B&W`. Blocked
 * prints drop the paper (they're excluded from totals); framed prints read
 * `5×7 (1) framed`.
 */
export function photoConfigSummary(data: PricingData, configPrints: readonly UiPrint[]): string {
  return configPrints
    .map((p) => {
      const size = sizeIdShort(p.sizeId);
      if (p.frameId) return `${size} (${p.qty}) framed${p.bw ? ' B&W' : ''}`;
      if (isBlockedPrint(p)) return `${size} (${p.qty})`;
      const base = `${size} (${p.qty}) ${shortPaperLabel(data, p.paper)}`;
      const tag = chipBorderTag(p);
      const withTag = tag ? `${base} ${tag}` : base;
      return p.bw ? `${withTag} B&W` : withTag;
    })
    .join(' · ');
}

/**
 * One print's config WITHOUT its quantity — the row label in the per-print
 * quantity editor (round 9), where the stepper already shows the number:
 * `4×6 Gloss` / `8×10 Gloss WB` / `5×7 framed` / `Custom 11.5×17 Pearl B&W`.
 */
export function printLineLabel(data: PricingData, p: UiPrint): string {
  const size = sizeIdShort(p.sizeId);
  if (p.frameId) return `${size} framed${p.bw ? ' B&W' : ''}`;
  if (isBlockedPrint(p)) return size;
  const base = `${size} ${shortPaperLabel(data, p.paper)}`;
  const tag = chipBorderTag(p);
  const withTag = tag ? `${base} ${tag}` : base;
  return p.bw ? `${withTag} B&W` : withTag;
}

/**
 * `59 photos · 4×6 (1) Gloss each` group-row caption / `4×6 (2) Gloss` single.
 * Round 7 reconciliation: when a group row carries BLOCKED (to-review,
 * not-counted) prints, the headline counts only the PRICED photos — "32
 * photos · 16×16 (1) each" — so the row's price ÷ count matches; the excluded
 * prints get their own amber "+50 need review · not counted yet" line.
 */
export function rowLabel(data: PricingData, row: PhotoOrderRow): string {
  const summary = photoConfigSummary(data, row.configPrints);
  if (row.photoIds.length <= 1) return summary;
  const count = row.blocked > 0 ? row.pricedPhotoCount : row.photoIds.length;
  return `${count} photo${count === 1 ? '' : 's'} · ${summary} each`;
}

/** The amber not-counted line for a row, or null: `+50 need review · not counted yet`. */
export function rowBlockedLabel(row: PhotoOrderRow): string | null {
  if (row.blocked === 0) return null;
  return `+${row.blocked} need${row.blocked === 1 ? 's' : ''} review · not counted yet`;
}
